import React, { useState, useEffect, useRef, useContext } from 'react';
import Moment from 'moment';
import { toast } from 'react-toastify';
import {
    get as _get,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from '@mui/material/LinearProgress';
import Backdrop from '@mui/material/Backdrop';
import { DataGridPro, useGridApiRef as useApiRef } from '@mui/x-data-grid-pro';

import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import UnscheduleIcon from '@mui/icons-material/CancelScheduleSend';
import AddIcon from '@mui/icons-material/Add';

import DateTimePicker from '../../../components/styled/datetimepicker';
import ConfirmationDialog from '../../../components/confirmationdlg';

import UpgradeService from '../../../data/upgrade-service';
import Auth, { ProfileContext } from '../../../data/auth-service';

import useWindowSize from '../../../libraries/usewindowsize';
import Helpers from '../../../libraries/helpers';

import { purple, normal } from '../../../data/colors';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    progressLabel: {
        position: 'relative',
        zIndex: '10',
        top: '38px',
        width: '100%',
        marginTop: '-50px',
        textAlign: 'center',
    },
    progressContainer: {
        width: '100%',
    },
    progressHeight: {
        height: '30px',
        borderRadius: '3px',
        marginTop: '30px',
    },
    progressBar: {
        height: '25px',
        borderRadius: '3px',
    },
    colorPrimary: {
        background: purple[400],
        height: '30px',
        borderRadius: '3px',
    },
    statusMessage: {
        position: 'relative',
        top: '55px',
        zIndex: 10,
        marginTop: '-50px',
        textAlign: 'center'
    },
    colorPrimaryError: {
        background: theme.palette.error.main,
        height: '30px',
        borderRadius: '3px',
    },
    colorPrimaryDone: {
        background: normal,
        height: '30px',
        borderRadius: '3px',
    },
    colorPrimaryUploading: {
        background: purple[200],
        height: '30px',
        borderRadius: '3px',
    },
    tableHeaderActions: {
        border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]}`,
        borderBottom: 'none',
    },
    popoverContainer: {
        background: theme.palette.common.black,
        border: `2px solid ${theme.palette.background.paper}`,
        bordeRadius: '3px',
    },
    backdrop: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 10000,
    },
    hideBox: {
        height: '0px',
        width: '0px',
        overflow: 'hidden',
    },
}));

const MC2Upgrade = () => {
    const classes = useStyles();
    const theme = useTheme();
    const [preStage, setPreStage] = useState([]);
    const [upgrades, setUpgrades] = useState([]);
    const [staged, setStaged] = useState([]);
    const [deployed, setDeployed] = useState([]);
    const [schedule, setSchedule] = useState([]);
    const [uploadFile, setUploadFile] = useState({ name: '', content: null });
    const [selectedFile, setSelectedFile] = useState(null);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [scheduleTemp, _setScheduleTemp] = useState(Moment());
    const scheduleTempRef = useRef(scheduleTemp);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [openDeleteStaged, setOpenDeleteStaged] = useState(false);
    const [openDeleteDeployed, setOpenDeleteDeployed] = useState(false);
    const [openDeploy, setOpenDeploy] = useState(false);
    const [deleteMenu, setDeleteMenu] = useState(null);
    const [openUnschedule, setOpenUnschedule] = useState(false);
    const [openSaveSchedule, setOpenSaveSchedule] = useState(false);
    const { height } = useWindowSize();
    const [, setOriginalStaged] = useState([]);
    const [, setOriginalDeployed] = useState([]);
    const tableAPIRef = useApiRef();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const [columnWidth, setColumnWidth] = useState({});
    const mc2UpgradePerms = Auth.permissions.get('Mc2Upgrades');

    const setScheduleTemp = (state) => {
        scheduleTempRef.current = state;
        _setScheduleTemp(state);
    };

    useEffect(() => {
        getUpgradeFiles();
        getUpgradeSchedule();
        setColumnWidth(getProfile('mc2_upgrade').widths || {});
    }, []);

    useEffect(() => {
        if (uploadFile && uploadFile.name) {
            setPreStage({
                id: Helpers.UniqueID(),
                name: uploadFile.name,
                modified: new Date().toISOString(),
                status: 'Uploading',
                progress: 0,
                prestage: true,
            });
            handleUploadFile();
        }
    }, [uploadFile]);

    const handleColumnResize = params => {
        const widths = tableAPIRef.current.getAllColumns().reduce((a, c) => {
            a[c.headerName] = c.width;
            return a;
        }, {});
        updateProfile('mc2_upgrade', {
            ...getProfile('mc2_upgrade'),
            widths: widths,
        });
        setColumnWidth(widths);
    };

    const getUpgradeFiles = () => {
        UpgradeService.listUpgradeFiles('mc2', 'default').then(res => {
            let u = [];

            if (res.stagedFiles) {
                const s = res.stagedFiles.map(v => ({ ...v, progress: 100, status: 'Complete', id: Helpers.UniqueID() }));
                setOriginalStaged(_cloneDeep(s));
                setStaged(s);
                u = _cloneDeep(s);
            } else {
                setOriginalStaged([]);
                setStaged([]);
            }
            if (res.deployedFiles) {
                const d = res.deployedFiles.map(v => ({ ...v, id: Helpers.UniqueID() }));
                setOriginalDeployed(_cloneDeep(d));
                setDeployed(d);
                d.forEach((v) => {
                    if (u.filter(x => x.name === v.name).length) {
                        return;
                    }
                    u.push(v);
                });
            } else {
                setOriginalDeployed([]);
                setDeployed([]);
            }
            setUpgrades(u);
        }, err => {
            toast.error(err);
        });
    };

    const getUpgradeSchedule = () => {
        UpgradeService.getUpgradeSchedule('mc2', 'default').then(res => {
            setSchedule(res);
        }, err => {
            toast.error(err);
        });
    };

    const handleGetFile = (e) => {
        setUploadFile(e.target.files[0]);
    };

    const handleUploadFile = () => {
        UpgradeService.uploadUpgradeFile('mc2', uploadFile).then(() => {
            toast.success('Install file uploaded successfully');
            getUpgradeFiles();
            getUpgradeSchedule();
            setPreStage([]);
            setSelectedFile(null);
            setUploadFile({ name: '', content: null });
        }, err => {
            if (err) {
                if (err.message && err.error) {
                    toast.error(`${err.message.replace('erorr', 'error')}:  ${err.error}`);
                } else if (err.message) {
                    toast.error(err.message);
                } else if (err.error) {
                    toast.error(err.error);
                } else {
                    toast.error(err);
                }
            }
            setPreStage([]);
            setSelectedFile(null);
            setUploadFile({ name: '', content: null });
        });
    };

    const tableRenderers = {
        fileName: (d) => { return <div>{d.row.name}</div> },
        updated: (d) => { return <div>{Moment(d.row.modified).format(theme.date.long)}</div> },
        scheduled: (d) => {
            if (schedule.upgrade_confs && schedule.upgrade_confs.upgrade_schedule) {
                if (schedule.upgrade_confs.upgrade_schedule.filename === d.row.name) {
                    return <div>{Moment(schedule.upgrade_confs.upgrade_schedule.datetimets).format(theme.date.long)}</div>;
                }
            }
            return <div>Not scheduled</div>;
        },
        progress: (d) => {
            return (
                <Box className={classes.progressContainer}>
                    <Box className={classes.statusMessage}>{d.row.status || 'Complete'}</Box>
                    <LinearProgress
                        variant={d.row.status !== 'Uploading' ? 'determinate' : 'indeterminate'}
                        value={100}
                        classes={{
                            barColorPrimary: d.row.status !== 'Uploading' ? (d.row.status === 'Failed' ? classes.colorPrimaryError : classes.colorPrimaryDone) : classes.colorPrimaryUploading,
                            determinate: classes.progressHeight,
                            indeterminate: classes.progressHeight,
                        }}
                    />
                </Box>
            );
        },
        deployed: (d) => {
            const i = deployed.findIndex(v => v.name === d.row.name && !d.row.prestage);
            return (
                <Box>
                    {i !== -1 ? Moment(deployed[i].modified).format(theme.date.long) : 'Not Deployed'}
                </Box>
            );
        },
    };

    const handleDeleteStagedFile = () => {
        handleToggleDeleteStaged();
        UpgradeService.deleteUpgradeFile('mc2', 'Staged', selectedFile.name, 'default').then(res => {
            toast.success('Staged file deleted successfully', {
                onOpen: () => {
                    getUpgradeFiles();
                    setPreStage([]);
                    setSelectedFile(null);
                }
            });
            if (mc2UpgradePerms.write) {
                UpgradeService.updateUpgradeSchedule('mc2', 'default', {}).then(res => {
                    getUpgradeSchedule();
                });
            }
        });
    };

    const handleDeleteDeployedFile = () => {
        handleToggleDeleteDeployed();
        UpgradeService.deleteUpgradeFile('mc2', 'Deployed', selectedFile.name, 'default').then(res => {
            toast.success('Deployed file deleted successfully', {
                onOpen: () => {
                    getUpgradeFiles();
                    getUpgradeSchedule();
                    setSelectedFile(null);
                },
            });
        });
    };

    const handleDeployStagedFile = () => {
        handleToggleDeploy();
        toast.info('Starting MC2 upgrade deployment...');
        setIsUpgrading(true);
        UpgradeService.deployUpgradeFile('mc2', selectedFile.name, 'default').then(res => {
            handleCheckStatus();
        }, err => {
            toast.error(err);
        });
    };

    const handleScheduleStagedFile = (e) => {
        setScheduleTemp(Moment().add(10, 'minutes'));
        setScheduleOpen(true);
    };

    const handleCloseSchedulePopup = () => {
        setScheduleOpen(false);
    };

    const handleUnscheduleStagedFile = () => {
        handleToggleUnschedule();
        UpgradeService.updateUpgradeSchedule('mc2', 'default', {}).then(res => {
            getUpgradeSchedule();
            toast.success(`Unscheduled ${selectedFile.name} successfully`);
        }, err => {
            toast.error(err);
        });
    };

    const handleSetSelected = (s) => {
        setSelectedFile(s.row);
    };

    const handleUpdateSchedule = (a, b, c) => {
        setScheduleTemp(a);
    };

    const handleCancelScheduleUpdate = () => {
        handleCloseSchedulePopup();
    };

    const handleSaveScheduleUpdate = () => {
        setOpenSaveSchedule(false);
        UpgradeService.updateUpgradeSchedule('mc2', 'default', {
            upgrade_confs: {
                upgrade_schedule: {
                    datetimets: scheduleTemp.toISOString(),
                    filename: selectedFile.name
                }
            }
        }).then(res => {
            toast.success('Schedule updated successfully');
            getUpgradeSchedule();
            handleCloseSchedulePopup();
        }, err => {
            toast.error(err);
            toast.error(`${err.message} ${err.error}`);
        });
    };

    let checkInterval;
    const handleCheckStatus = () => {
        checkInterval = setInterval(() => {
            UpgradeService.getUpgradeStatus('mc2').then(() => {
                clearInterval(checkInterval);
                toast.success('MC2 upgraded successfully');
                setIsUpgrading(false);
                getUpgradeFiles();
                getUpgradeSchedule();
            });
        }, 10000);
    };

    const handleToggleDeleteStaged = () => {
        setOpenDeleteStaged(!openDeleteStaged);
    };

    const handleToggleDeleteDeployed = () => {
        setOpenDeleteDeployed(!openDeleteDeployed);
    };

    const handleToggleDeploy = () => {
        setOpenDeploy(!openDeploy);
    };

    const handleToggleUnschedule = () => {
        setOpenUnschedule(!openUnschedule);
    };

    const handleToggleSchedule = () => {
        setOpenSaveSchedule(!openSaveSchedule);
    };

    const handleToggleDeleteMenu = (e) => {
        setDeleteMenu(e.currentTarget);
    };

    const handleCloseDeleteMenu = () => {
        setDeleteMenu(null);
    };

    return (
        <Grid container spacing={2}>
            <Grid container item xs={12} justifyContent="flex-end">
                {mc2UpgradePerms.write && <Box>
                    <input
                        id="mc2-upgrade-file"
                        style={{ display: 'none' }}
                        type="file"
                        onClick={(e) => { e.target.value = null }}
                        onChange={handleGetFile}
                    />
                    <label htmlFor="mc2-upgrade-file">
                        <Button
                            variant="contained"
                            color="primary"
                            component="span"
                            aria-label="upload"
                        ><AddIcon /> Upload</Button>
                    </label>
                </Box>}
            </Grid>
            <Grid container item xs={12}>
                <Box width="100%" height={height - 310}>
                    <Box className={classes.tableHeaderActions} p={1} display="flex" justifyContent="flex-end">
                        {mc2UpgradePerms.delete && <Box display="flex">
                            <Box ml={2}>
                                <Tooltip title="Delete">
                                    <span>
                                        <IconButton
                                            disabled={!selectedFile}
                                            color="secondary"
                                            aria-label="delete"
                                            onClick={handleToggleDeleteMenu}
                                            size="large">
                                            <DeleteIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Menu open={Boolean(deleteMenu)} anchorEl={deleteMenu} onClose={handleCloseDeleteMenu}>
                                    <Box display={selectedFile ? (staged.findIndex(v => v.name === selectedFile.name) !== -1) ? 'block' : 'none' : 'none'}>
                                        <MenuItem onClick={handleToggleDeleteStaged}>Delete Staged</MenuItem>
                                    </Box>
                                    <Box display={selectedFile ? (deployed.findIndex(v => v.name === selectedFile.name) !== -1) ? 'block' : 'none' : 'none'}>
                                        <MenuItem onClick={handleToggleDeleteDeployed}>Delete Deployed</MenuItem>
                                    </Box>
                                </Menu>
                            </Box>
                        </Box>}
                        {mc2UpgradePerms.admin && <Box display="flex">
                            <Box ml={2}>
                                <Tooltip title="Cancel Schedule">
                                    <span>
                                        <IconButton
                                            disabled={(() => {
                                                return !selectedFile || !(_get(schedule, 'upgrade_confs.upgrade_schedule.filename') === selectedFile.name);
                                            })()}
                                            color="secondary"
                                            aria-label="Unschedule"
                                            onClick={handleToggleUnschedule}
                                            size="large">
                                            <UnscheduleIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                            <Box ml={2}>
                                <Tooltip title="Schedule">
                                    <span>
                                        <IconButton
                                            disabled={!selectedFile}
                                            color="primary"
                                            aria-label="schedule"
                                            onClick={handleScheduleStagedFile}
                                            size="large">
                                            <ScheduleIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Box className={classes.hideBox}>
                                    <DateTimePicker
                                        onChange={handleUpdateSchedule}
                                        value={scheduleTemp}
                                        disablePast
                                        open={scheduleOpen}
                                        onClose={handleCancelScheduleUpdate}
                                        onAccept={handleToggleSchedule}
                                    />
                                </Box>
                            </Box>
                            <Box ml={2}>
                                <Tooltip title="Deploy">
                                    <Box mt={1}>
                                        <Button
                                            disabled={!selectedFile}
                                            color="primary"
                                            aria-label="deploy"
                                            onClick={handleToggleDeploy}
                                        >
                                            Deploy
                                        </Button>
                                    </Box>
                                </Tooltip>
                            </Box>
                        </Box>}
                    </Box>
                    <DataGridPro
                        apiRef={tableAPIRef}
                        rows={Array.prototype.concat(preStage, upgrades)}
                        columns={[
                            { sortable: true, width: columnWidth['File Name'] || 0, ...(!columnWidth['File Name'] ? { flex: 1 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'File Name', field: 'file_name', renderCell: tableRenderers.fileName },
                            { sortable: true, width: columnWidth.Uploaded || 0, ...(!columnWidth.Uploaded ? { flex: 1 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Uploaded', field: 'create', renderCell: tableRenderers.updated },
                            { sortable: true, width: columnWidth.Scheduled || 0, ...(!columnWidth.Scheduled ? { flex: 1 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Scheduled', field: 'updated', renderCell: tableRenderers.scheduled },
                            { sortable: true, width: columnWidth['Upload Progress'] || 0, ...(!columnWidth['Upload Progress'] ? { flex: 0.7 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Upload Progress', field: 'progress', renderCell: tableRenderers.progress },
                            { sortable: true, width: columnWidth['Deployed At'] || 0, ...(!columnWidth['Deployed At'] ? { flex: 0.7 } : {}), type: 'string', disableColumnMenu: true, hidden: false, headerName: 'Deployed At', field: 'deployed', renderCell: tableRenderers.deployed },
                        ]}
                        disableMultipleColumnsSorting
                        disableMultipleSelection
                        onRowClick={handleSetSelected}
                        onColumnResize={handleColumnResize}
                    />
                </Box>
            </Grid>
            <Backdrop className={classes.backdrop} open={isUpgrading}>
                <Paper elevation={3}>
                    <LinearProgress />
                    <Box p={4}>
                        <Box><Typography>Please wait for the MC2 to finish upgrading...</Typography></Box>
                        <Box mt={2}><Typography>Running upgrade for MC2 with file {selectedFile && selectedFile.name}</Typography></Box>
                    </Box>
                </Paper>
            </Backdrop>
            <ConfirmationDialog
                onClose={handleToggleDeleteStaged}
                onOK={handleDeleteStagedFile}
                open={openDeleteStaged}
                message={`Are you sure you want to delete the staged file ${selectedFile && selectedFile.name}?`}
                title="Delete staged file"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleToggleDeleteDeployed}
                onOK={handleDeleteDeployedFile}
                open={openDeleteDeployed}
                message={`Are you sure you want to delete the deployed file ${selectedFile && selectedFile.name}?`}
                title="Delete deployed file"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleToggleDeploy}
                onOK={handleDeployStagedFile}
                open={openDeploy}
                message={`Are you sure you want to deploy the file ${selectedFile && selectedFile.name} for upgrade?`}
                title="Deploy staged file for upgrade"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleToggleUnschedule}
                onOK={handleUnscheduleStagedFile}
                open={openUnschedule}
                message={`Are you sure you want to unschedule ${selectedFile && selectedFile.name} for deployment?`}
                title="Unschedule"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
            <ConfirmationDialog
                onClose={handleToggleSchedule}
                onOK={handleSaveScheduleUpdate}
                open={openSaveSchedule}
                message={`Are you sure you want to schedule ${selectedFile && selectedFile.name} for deployment at ${scheduleTemp.format(theme.date.long)}?`}
                title="Schedule for deployment"
                cancelLabel="Cancel"
                okLabel="Yes"
            />
        </Grid>
    );
};

export default MC2Upgrade;
